import React from 'react';
import isEqual from 'react-fast-compare';
import {ChartQuery, ChartSettings} from 'entities/chart';
import {FormikT} from 'utils/types';
import {ChartType} from '@/views/storyboard/utils/chartConstants';
import {hasVisibleAdvancedSettings} from '@/views/storyboard/utils/chartSettingsFieldVisibility';
import ChartSettingsOptions, {ChartSettingsOptionsSection} from '@/views/storyboard/chart_settings/ChartSettingsOptions';
import ChartSettingsPanel from '@/views/storyboard/chart_settings/ChartSettingsPanel';
import {
    ChartSettingsPreviewChart,
    ChartSettingsPreviewFooter,
    ChartSettingsPreviewHeader,
} from '@/views/storyboard/chart_settings/ChartSettingsPreview';
import {ChartSettingsSizeControls} from '@/views/storyboard/chart_settings/ChartSettingsSizeControls';
import {ChartSettingsTitleEditorRef} from '@/views/storyboard/chart_settings/ChartSettingsTitleEditor';
import {ChartFooterData} from '@/views/storyboard/charts/utils/chartFooterData';
import i18n from 'i18n';

interface Props {
    chartQueryFormik: FormikT<ChartQuery>;
    chartSettingsFormik: FormikT<ChartSettings>;
    chartTitleInputRef?: React.RefObject<ChartSettingsTitleEditorRef>;
    showTitleRequiredError?: boolean;
    previewDatePeriod: string;
    previewFooterData: ChartFooterData;
    onFooterDataChange: (footerData: ChartFooterData) => void;
}

const ChartSettingsPreviewColumn = React.memo<Props>(({
    chartQueryFormik,
    chartSettingsFormik,
    chartTitleInputRef,
    showTitleRequiredError = false,
    previewDatePeriod,
    previewFooterData,
    onFooterDataChange,
}) => {
    const showAdvancedSettings = hasVisibleAdvancedSettings(
        chartSettingsFormik.values.type as ChartType,
        chartQueryFormik.values.output,
        chartSettingsFormik.values,
    );

    return (
    <div className="chart-settings-form-output-hero">
        <div className="chart-settings-form-section__title chart-settings-form-section__title--numbered">
            <span className="chart-settings-form-section__number">3</span>
            {i18n.t('chart_settings_section_preview.text')}
        </div>

        <ChartSettingsPanel variant="preview" className="chart-settings-preview-panel">
            <div className="chart-settings-preview-card">
                <ChartSettingsPreviewHeader
                    chartSettingsFormik={chartSettingsFormik}
                    showTitleRequiredError={showTitleRequiredError}
                    titleEditorRef={chartTitleInputRef}
                />
                <ChartSettingsPreviewChart
                    chartSettingsFormik={chartSettingsFormik}
                    chartQueryFormik={chartQueryFormik}
                    onFooterDataChange={onFooterDataChange}
                />
                <ChartSettingsPreviewFooter
                    datePeriod={previewDatePeriod}
                    footerData={previewFooterData}
                />
            </div>
        </ChartSettingsPanel>
        <ChartSettingsSizeControls chartSettingsFormik={chartSettingsFormik} />
        {showAdvancedSettings ? (
            <ChartSettingsOptionsSection
                title={i18n.t('chart_settings_section_advanced_settings.text')}
                sectionId="advanced-settings"
            >
                <ChartSettingsOptions
                    chartQueryFormik={chartQueryFormik}
                    chartSettingsFormik={chartSettingsFormik}
                />
            </ChartSettingsOptionsSection>
        ) : null}
    </div>
    );
}, (prevProps, nextProps) => (
    prevProps.showTitleRequiredError === nextProps.showTitleRequiredError
    && prevProps.previewDatePeriod === nextProps.previewDatePeriod
    && prevProps.previewFooterData === nextProps.previewFooterData
    && prevProps.onFooterDataChange === nextProps.onFooterDataChange
    && prevProps.chartTitleInputRef === nextProps.chartTitleInputRef
    && isEqual(prevProps.chartQueryFormik.values, nextProps.chartQueryFormik.values)
    && isEqual(prevProps.chartSettingsFormik.values, nextProps.chartSettingsFormik.values)
));

export default ChartSettingsPreviewColumn;
